import React, { memo, ReactElement, useState } from 'react';
import styled from '@emotion/styled';
import { useTheme } from '@emotion/react';
import { Box, Flex, Text } from '@updater/ui-uds';

interface NavButtonProps {
  icon: ReactElement;
  text: string;
  isActive: boolean;
  dot?: boolean;
  onClick: () => void;
}

type TextDecoratorTypes = 'none' | 'underline';
interface NavItemTextProps {
  textDecoration: TextDecoratorTypes;
}

const NavElement = styled(Flex)(
  ({ theme }) => `
  &:hover {
    cursor: pointer;
    ${theme.mediaQueries.l} {
      filter: brightness(0.8);
    }
  }
`
);

const IconWrapper = styled(Flex)<{ isActive: boolean }>`
  border-radius: ${({ theme }) => theme.radii.xs};
  background-color: ${({ isActive, theme }) =>
    isActive ? theme.colors.cta : theme.colors.navBarIconBackground};
`;

const NavItemText = styled(Text)<NavItemTextProps>(
  ({ theme, textDecoration }) => `
  text-decoration: none;
  ${theme.mediaQueries.l} {
    text-decoration: ${textDecoration};
  }
  line-height: normal !important;
`
);

export const NavBtn: React.FC<NavButtonProps> = ({
  icon,
  text,
  isActive,
  dot,
  onClick,
}) => {
  const [textDecoration, setTextDecoration] =
    useState<TextDecoratorTypes>('none');
  const theme = useTheme();

  const iconProps = {
    color: isActive ? theme.colors.navBarActiveItem : theme.colors.primary,
    size: '100%',
  };

  // This is done to pass additional props to the Icon
  const clonedIcon = React.cloneElement(icon, iconProps);

  return (
    <NavElement
      onClick={onClick}
      data-testid={text}
      as="button"
      height={['66px', '66px', '66px', 'unset']}
      flexDirection={['column', 'column', null, 'row']}
      alignItems="center"
      border="none"
      margin="none"
      padding="none"
      marginBottom={['none', null, null, 'l', null, 'xl']}
      marginTop={['xxs', 'xxs', null]}
      justifyContent={['space-around', 'space-around', null, 'initial']}
      backgroundColor="transparent"
      width={['unset', 'unset', null, '100%']}
      onMouseEnter={() => setTextDecoration('underline')}
      onMouseLeave={() => setTextDecoration('none')}
    >
      <IconWrapper
        isActive={isActive}
        justifyContent="center"
        alignItems="center"
        width={['40px', '40px', '56px', '40px', '40px']}
        height={['40px', '40px', '56px', '40px', '40px']}
        margin="none"
        marginRight={['none', 'none', null, 's']}
        position="relative"
      >
        <Flex
          width={['23px', '23px', '28px', '23px', '23px']}
          height={['23px', '23px', '28px', '23px', '23px']}
          justifyContent="center"
          alignItems="center"
        >
          {clonedIcon}
        </Flex>
      </IconWrapper>
      <Flex alignItems="center">
        <NavItemText
          variant={[
            isActive ? 'xsBold' : 'xs',
            isActive ? 'xsBold' : 'xs',
            isActive ? 'xsBold' : 'xs',
            isActive ? 'lBold' : 'l',
            isActive ? 'lBold' : 'l',
          ]}
          as="p"
          color="primary"
          textDecoration={textDecoration}
        >
          {text}
        </NavItemText>
        {dot && (
          <Box
            data-testid="notification-dot"
            height="6px"
            width="6px"
            backgroundColor="brand"
            borderRadius="50%"
            marginLeft="xxs"
          />
        )}
      </Flex>
    </NavElement>
  );
};

export const NavButton = memo(NavBtn);
